import React, {
    useEffect,
    useState,
    lazy,
    Suspense,
    useCallback,
} from "react";

const EventSignupModal = lazy(() => import("./modals/EventSignupModal"));
import { Instagram, Facebook } from "lucide-react";
import { ALL_EVENTS } from "@/data/events";

const NAV_LINKS = [
    { href: "#about", label: "About" },
    { href: "#formats", label: "Formats" },
    { href: "#events", label: "Events" },
    { href: "#partners", label: "Partners" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
] as const;

const Header: React.FC = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [signupOpen, setSignupOpen] = useState(false);

    const preloadModal = useCallback(() => {
        import("./modals/EventSignupModal");
    }, []);

    const openSignup = useCallback(() => {
        preloadModal();
        setSignupOpen(true);
    }, [preloadModal]);

    const closeMenu = useCallback(() => setMenuOpen(false), []);

    useEffect(() => {
        document.documentElement.classList.toggle("overflow-hidden", menuOpen);
        return () => document.documentElement.classList.remove("overflow-hidden");
    }, [menuOpen]);

    return (
        <>
            <header className="fixed top-0 left-0 w-full bg-[#0B1E2E] text-white z-50 shadow-sm">
                <div className="container mx-auto px-6 py-4 flex items-center justify-between">
                    {/* Logo */}
                    <a href="#" className="text-3xl lg:text-4xl font-playfair select-none">
                        ZERO<span className="font-pinyon">club</span>
                    </a>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex gap-8 font-montserrat text-lg items-center">
                        {NAV_LINKS.map(({ href, label }) => (
                            <a
                                key={href}
                                href={href}
                                className="hover:text-accent-orange transition-colors"
                            >
                                {label}
                            </a>
                        ))}

                        <div className="flex items-center gap-4 mr-2">
                            <a
                                href="https://www.instagram.com/zeroclub__"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="hover:text-accent-orange transition-colors"
                            >
                                <Instagram size={26} />
                            </a>
                            <a
                                href="https://www.facebook.com/people/ZEROclub/61579936142622"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                className="hover:text-accent-orange transition-colors"
                            >
                                <Facebook size={22} />
                            </a>
                        </div>

                        <button
                            onClick={openSignup}
                            onPointerEnter={preloadModal}
                            onTouchStart={preloadModal}
                            className="relative overflow-hidden rounded-full font-montserrat font-medium bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 text-sm md:text-base md:px-5 md:py-2.5 transition-all duration-300 hover:from-orange-400 hover:to-orange-500 hover:shadow-2xl hover:shadow-orange-500/40 hover:scale-105 whitespace-nowrap"
                        >
                            Join Now
                        </button>
                    </nav>

                    {/* Mobile controls */}
                    <div className="flex items-center gap-3 lg:hidden">
                        <button
                            onClick={openSignup}
                            onPointerEnter={preloadModal}
                            onTouchStart={preloadModal}
                            className="relative overflow-hidden rounded-full font-montserrat font-medium bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 text-sm md:text-base md:px-5 md:py-2.5 transition-all duration-300 hover:from-orange-400 hover:to-orange-500 hover:shadow-2xl hover:shadow-orange-500/40 hover:scale-105 whitespace-nowrap"
                        >
                            Join Now
                        </button>
                        <button
                            aria-label="Toggle menu"
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen(v => !v)}
                            className="relative w-9 h-9"
                        >
                            <span
                                className={`absolute block h-0.5 w-6 bg-white transition-transform duration-300 ${menuOpen ? "translate-y-0 rotate-45" : "-translate-y-2"}`}
                            />
                            <span
                                className={`absolute block h-0.5 w-6 bg-white transition-opacity duration-200 ${menuOpen ? "opacity-0" : "opacity-100"}`}
                            />
                            <span
                                className={`absolute block h-0.5 w-6 bg-white transition-transform duration-300 ${menuOpen ? "translate-y-0 -rotate-45" : "translate-y-2"}`}
                            />
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                <div className="lg:hidden">
                    <nav
                        className={`absolute left-0 right-0 bg-navy-primary text-white border-t border-white/10 transition-all duration-300 ${
                            menuOpen
                                ? "opacity-100 translate-y-0"
                                : "opacity-0 -translate-y-3 pointer-events-none"
                        }`}
                    >
                        <div className="flex items-center gap-5 px-6 pt-5 pb-2">
                            <a
                                href="https://www.instagram.com/zeroclub__"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="hover:text-accent-orange transition-colors"
                            >
                                <Instagram size={26} />
                            </a>
                            <a
                                href="https://www.facebook.com/people/ZEROclub/61579936142622"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                className="hover:text-accent-orange transition-colors"
                            >
                                <Facebook size={22} />
                            </a>
                        </div>

                        <ul className="flex flex-col gap-2 px-6 py-5 font-montserrat text-base">
                            {NAV_LINKS.map(({ href, label }) => (
                                <li key={href}>
                                    <a
                                        href={href}
                                        className="block py-2 hover:text-accent-orange transition-colors"
                                    >
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
            </header>

            {/* Backdrop */}
            <button
                aria-hidden={!menuOpen}
                onClick={closeMenu}
                className={`fixed inset-0 bg-black/50 backdrop-blur-[1px] z-40 transition-opacity duration-300 ${
                    menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
            />

            {/* Modal */}
            <Suspense fallback={null}>
                <EventSignupModal
                    open={signupOpen}
                    onClose={() => setSignupOpen(false)}
                    events={ALL_EVENTS}
                    defaultEventId={undefined}
                />
            </Suspense>
        </>
    );
};

export default Header;